import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { Experience } from '../../data/experiences';
import { trackBookingOpen, type BookingSource } from '../../lib/analytics';
import { BookingContext } from './BookingContext';
import { BookingModal } from './BookingModal';

interface OpenState {
  experience?: Experience;
  optionId?: number;
  optionAlias?: string;
}

/**
 * Hosts the single booking modal and exposes `openBooking(...)` to the whole
 * app via context. Drop once near the app root.
 */
export function BookingProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<OpenState | null>(null);

  const openBooking = useCallback(
    (
      experience?: Experience,
      optionId?: number,
      source: BookingSource = 'unknown',
      optionAlias?: string,
    ) => {
      trackBookingOpen(source, experience);
      setOpen({ experience, optionId, optionAlias });
    },
    [],
  );

  const value = useMemo(() => ({ openBooking }), [openBooking]);

  return (
    <BookingContext.Provider value={value}>
      {children}
      {open && (
        <BookingModal
          initialExperience={open.experience}
          initialOptionId={open.optionId}
          initialOptionAlias={open.optionAlias}
          onClose={() => setOpen(null)}
        />
      )}
    </BookingContext.Provider>
  );
}
